import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Field, Input } from '@w7/ui'
import { cmIpAllowlistDeniedMessage, isCmIpAllowlistDenied } from '@/auth/cmIpAllowlist'
import { getAuthConfig, isOidcConfigured } from '@/auth/config'
import { beginOidcLogin, rememberNext } from '@/auth/oidc'
import { readAuth } from '@/auth/session'
import '@/styles/app.css'

function normalizeUsername(value: string): string {
  return value.trim().toLowerCase()
}

/**
 * Same Keycloak OIDC PKCE path for CM owners and Access Level staff.
 * Staff identities are provisioned in Keycloak on Access Level create/update.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const cfg = useMemo(() => getAuthConfig(), [])
  const oidcReady = isOidcConfigured(cfg)

  const [username, setUsername] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reason = params.get('reason')
  const next = params.get('next') || '/home'
  const safeNext = next.startsWith('/') ? next : '/home'

  useEffect(() => {
    if (readAuth()) navigate(safeNext, { replace: true })
  }, [navigate, safeNext])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (reason === 'denied') {
        setError('Sign-in was cancelled or CM access was denied.')
        return
      }
      if (reason === 'error') {
        setError('Sign-in failed. Try again.')
        return
      }
      if (reason === 'expired') {
        setError('Session expired. Sign in again.')
        return
      }
      if (reason === 'unauthorised') {
        setError('Unauthorised Access')
        return
      }
      const ipDenied = reason === 'ip' || (await isCmIpAllowlistDenied())
      if (cancelled || !ipDenied) return
      let publicIp: string | null = null
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json')
        const ipJson = (await ipRes.json()) as { ip?: string }
        publicIp = typeof ipJson.ip === 'string' ? ipJson.ip : null
      } catch {
        publicIp = null
      }
      if (!cancelled) setError(cmIpAllowlistDeniedMessage(publicIp))
    })()
    return () => {
      cancelled = true
    }
  }, [reason])

  useEffect(() => {
    const clearBusy = () => setBusy(false)
    clearBusy()
    const onPageShow = () => clearBusy()
    const onVisible = () => {
      if (document.visibilityState === 'visible') clearBusy()
    }
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('focus', clearBusy)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('focus', clearBusy)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const hint = normalizeUsername(username)
    if (!hint) {
      setError('Username or email is required')
      return
    }

    rememberNext(safeNext)
    setBusy(true)

    if (!oidcReady) {
      setBusy(false)
      setError('Sign-in is unavailable. Configure Keycloak for this console.')
      return
    }
    try {
      await beginOidcLogin(hint)
      setBusy(false)
    } catch (ex) {
      setBusy(false)
      setError(ex instanceof Error ? ex.message : 'Sign-in could not start.')
    }
  }

  return (
    <div className="sm-login">
      <div className="sm-login-bg" aria-hidden="true" />
      <div className="sm-login-card">
        <div className="sm-login-brand">
          <img src="/brand/strix-logo.png" alt="" width={40} height={40} decoding="async" />
          <div>
            <strong>Company Master</strong>
            <span>CM console</span>
          </div>
        </div>
        <h1>Sign in</h1>
        <p className="sm-login-lede">
          Company Master and Access Level staff use the same Keycloak sign-in. Enter your username
          or email, then continue to enter your password on Keycloak.
        </p>

        {error ? (
          <p className="sm-login-alert" role="alert">
            {error}
          </p>
        ) : null}

        <form className="sm-login-form" onSubmit={(e) => void onSubmit(e)}>
          <Field label="Email or username" required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                name="username"
                aria-describedby={describedBy}
                invalid={invalid}
                value={username}
                onChange={(ev) => {
                  setUsername(ev.target.value)
                  if (error) setError(null)
                }}
                placeholder="Enter your email or username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                disabled={busy}
                required
              />
            )}
          </Field>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  )
}
